import { Link } from "react-router-dom";
import { Check, Minus, ArrowUpRight } from "lucide-react";
import { formatPrice } from "./ProductCardV2";

/**
 * Comparison table — used on /shop to help visitors decide which
 * product fits their main concern. Columns: Product / Best for /
 * Signals it supports (Energy, Weight, Gut, Sleep, Focus, Hormone).
 *
 * Mobile: collapses to a stacked card layout so no horizontal scroll.
 *
 * Signals are derived from `product.wellness_signals` (set by the admin)
 * — each canonical signal maps to one of the comparison columns.
 */
const COLUMNS = [
  { key: "energy",   label: "Energy",   signals: ["energy", "fatigue", "burnout_signal"] },
  { key: "weight",   label: "Weight",   signals: ["weight_loss", "fat_loss", "metabolism", "metabolism_support", "metabolic_health"] },
  { key: "gut",      label: "Gut",      signals: ["gut_health", "digestive_support", "bloating", "ibs"] },
  { key: "sleep",    label: "Sleep",    signals: ["sleep", "sleep_support", "calm", "relaxation"] },
  { key: "focus",    label: "Focus",    signals: ["focus", "mental_focus", "brain_fog", "clarity"] },
  { key: "hormone",  label: "Hormones", signals: ["hormonal_support", "menopause_support", "pcos", "fertility"] },
];

function supports(product, col) {
  const signals = (product.wellness_signals || product.tags || []).map((s) => (s || "").toLowerCase());
  return col.signals.some((s) => signals.includes(s));
}

export default function ComparisonTable({ products }) {
  if (!products || products.length === 0) return null;

  return (
    <section
      className="bg-white border border-emerald-100 rounded-3xl overflow-hidden"
      data-testid="shop-comparison-table"
    >
      <div className="p-6 sm:p-8 border-b border-emerald-100">
        <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
          Compare at a glance
        </p>
        <h2 className="font-display text-2xl sm:text-3xl text-emerald-950 font-light leading-tight mt-1">
          Which products support what you're feeling?
        </h2>
        <p className="text-sm text-emerald-900/65 mt-2 max-w-xl leading-relaxed">
          A simple side-by-side. We never claim to treat anything — these are the wellness signals each product is most often used for.
        </p>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="shop-comparison-table-desktop">
          <thead className="text-left">
            <tr className="border-b border-emerald-100 bg-emerald-50/40">
              <th className="px-6 py-3 text-[11px] uppercase tracking-wider font-semibold text-emerald-700">Product</th>
              <th className="px-3 py-3 text-[11px] uppercase tracking-wider font-semibold text-emerald-700">Best for</th>
              {COLUMNS.map((c) => (
                <th key={c.key} className="px-3 py-3 text-[11px] uppercase tracking-wider font-semibold text-emerald-700 text-center">
                  {c.label}
                </th>
              ))}
              <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-emerald-700 text-right">Price</th>
              <th aria-hidden></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-emerald-50 hover:bg-emerald-50/30 transition" data-testid={`shop-compare-row-${p.slug}`}>
                <td className="px-6 py-4 align-top">
                  <Link to={`/shop/${p.slug}`} className="text-emerald-950 font-semibold hover:text-emerald-700">
                    {p.name}
                  </Link>
                  <div className="text-[11px] text-emerald-900/55 mt-0.5 capitalize">{(p.category || "supplement").replace(/_/g, " ")}</div>
                </td>
                <td className="px-3 py-4 align-top max-w-[200px]">
                  {(p.best_for || []).slice(0, 3).map((b) => (
                    <span key={b} className="inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 mr-1 mb-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100">
                      {b}
                    </span>
                  ))}
                </td>
                {COLUMNS.map((c) => (
                  <td key={c.key} className="px-3 py-4 align-middle text-center">
                    {supports(p, c) ? (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-700">
                        <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-stone-50 text-stone-300">
                        <Minus className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </td>
                ))}
                <td className="px-4 py-4 align-top text-right whitespace-nowrap">
                  <span className="font-display text-base text-emerald-950">{formatPrice(p.price, p.currency)}</span>
                </td>
                <td className="px-4 py-4 align-middle text-right">
                  <Link to={`/shop/${p.slug}`} className="inline-flex items-center text-emerald-700 hover:text-emerald-800 text-sm" data-testid={`shop-compare-cta-${p.slug}`}>
                    Read <ArrowUpRight className="w-3.5 h-3.5 ml-0.5" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile stacked cards */}
      <div className="md:hidden divide-y divide-emerald-50">
        {products.map((p) => (
          <div key={p.id} className="p-5" data-testid={`shop-compare-mobile-${p.slug}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <Link to={`/shop/${p.slug}`} className="text-base font-semibold text-emerald-950">{p.name}</Link>
                <div className="text-[11px] text-emerald-900/55 mt-0.5 capitalize">{(p.category || "supplement").replace(/_/g, " ")}</div>
              </div>
              <span className="font-display text-base text-emerald-950">{formatPrice(p.price, p.currency)}</span>
            </div>
            {(p.best_for || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {p.best_for.slice(0, 4).map((b) => (
                  <span key={b} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100">
                    {b}
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {COLUMNS.filter((c) => supports(p, c)).map((c) => (
                <span key={c.key} className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-800 bg-emerald-100/70 rounded-full px-2 py-0.5">
                  <Check className="w-3 h-3" strokeWidth={2.5} /> {c.label}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
